import { CARD_SLOTS, type CardSlot, cardDefinition, cardWorksIn } from '@kenocyber/shared';
import {
  type CardAbilityState,
  cardAbilityCooldownMs,
  cardAbilityDurationMs,
  cardAbilityIsImplementedIn,
} from './abilities';

/**
 * **O que os três espaços mostram, como estado puro** (gate V28H.I).
 *
 * ## Porque isto não vive no componente
 *
 * Porque "esta carta está disponível" é uma afirmação sobre a simulação, e não
 * sobre um `div`. Escrita aqui, ela pode ser provada sem DOM, sem React e sem
 * navegador - e a interface fica com uma única responsabilidade, que é desenhar
 * o que este ficheiro decidiu.
 *
 * ## Porque é neutro entre a caça e o duelo
 *
 * Ele recebe a lista de espaços e três factos sobre o mundo - vivo, o modo, e se
 * há sessão. Nada aqui sabe o que é um mob, um chefe ou um adversário. Quando a
 * primeira carta de PvP existir, a barra do duelo lê o mesmo modelo; o que
 * pertence a esse gate é o TRANSPORTE, e não esta aritmética.
 *
 * ## A ordem dos estados é uma decisão
 *
 * Um espaço vazio é vazio antes de tudo; uma carta que não funciona neste modo
 * está bloqueada mesmo que o arrefecimento tenha acabado; uma nave morta bloqueia
 * tudo o que ainda não está a correr - mas **não** interrompe o que já está
 * activo, porque uma habilidade a decorrer é estado da simulação e não uma
 * permissão da interface.
 */

/** O estado de um espaço. Cinco, e mutuamente exclusivos. */
export type CardSlotUiState = 'empty' | 'available' | 'active' | 'cooldown' | 'blocked';

/**
 * Porque um espaço está bloqueado. Sempre nomeado, nunca "porque sim".
 *
 * O contrato exige um motivo LEGÍVEL, e um motivo legível começa por ser um
 * motivo nomeado: a frase é da interface, a razão é daqui.
 */
export type CardSlotBlockedReason =
  /** A carta existe, mas não faz nada neste modo (por exemplo, só PvP). */
  | 'wrong_mode'
  /** A carta ainda não tem habilidade implementada. */
  | 'not_implemented'
  /** A nave está abatida. */
  | 'ship_dead'
  /** Não há caça a decorrer para receber a ordem. */
  | 'no_session';

/** Tudo o que a barra precisa de saber sobre UM espaço. */
export interface CardSlotUiView {
  readonly slot: CardSlot;
  /** 1, 2 ou 3 - o número da tecla, e a ordem em que a barra desenha. */
  readonly index: number;
  readonly definitionId: string | null;
  readonly instanceId: string | null;
  readonly level: number | null;
  readonly displayName: string | null;
  readonly state: CardSlotUiState;
  /**
   * Quanto falta do que está a correr, em ms.
   *
   * Zero quando não há nada a correr. É a duração restante quando o estado é
   * `active`, e o arrefecimento restante quando é `cooldown` - nunca os dois, o
   * que é a razão de ser um campo só.
   */
  readonly remainingMs: number;
  /**
   * O total de que `remainingMs` é uma fracção, em ms.
   *
   * Existe para o preenchimento circular ter um denominador que não depende de
   * a interface conhecer a carta. Zero quando não há nada a correr.
   */
  readonly totalMs: number;
  readonly blockedReason: CardSlotBlockedReason | null;
  /** Se uma ordem sobre este espaço pode ser enviada AGORA. */
  readonly actionable: boolean;
}

/** O mundo, tal como os espaços precisam de o conhecer. */
export interface CardHudContext {
  /** Os espaços congelados desta corrida, na ordem canónica. */
  readonly cards: readonly CardAbilityState[];
  /** `false` quando a nave está abatida. */
  readonly shipAlive: boolean;
  /** Há uma sessão autoritativa capaz de receber a ordem. */
  readonly hasSession: boolean;
  /** O modo em que estes espaços correm. */
  readonly mode: 'hunt' | 'pvp';
  /**
   * O arrefecimento TOTAL de cada espaço, quando ele não pode ser derivado.
   *
   * A caça deriva-o do catálogo: ela tem o nível congelado à mão. O duelo não -
   * o nível é inventário, e ele **não sai do servidor**; o que atravessa o fio é
   * o total já calculado. Este campo é por onde ele entra.
   *
   * Ausente quer dizer "deriva-o", que é o caso da caça.
   */
  readonly cooldownTotalsMs?: Readonly<Partial<Record<CardSlot, number>>>;
}

/**
 * Os TRÊS espaços, sempre três, pela ordem 1, 2, 3.
 *
 * Nunca uma lista curta porque um espaço está vazio: a barra desenha três
 * caixas, e uma lista que encolhe obrigá-la-ia a adivinhar quais faltam - a
 * mesma decisão que `cardSlotsFor` tomou no inventário.
 */
export function cardSlotUiViews(context: CardHudContext): readonly CardSlotUiView[] {
  return CARD_SLOTS.map((slot, position) => {
    const index = position + 1;
    const card = context.cards.find((entry) => entry.slot === slot) ?? null;
    if (!card) {
      return {
        slot,
        index,
        definitionId: null,
        instanceId: null,
        level: null,
        displayName: null,
        state: 'empty' as const,
        remainingMs: 0,
        totalMs: 0,
        blockedReason: null,
        actionable: false,
      };
    }

    const definition = cardDefinition(card.definitionId);
    const common = {
      slot,
      index,
      definitionId: card.definitionId,
      instanceId: card.instanceId,
      /*
       * O nível, dito nos DOIS modos (gate V28H.Q).
       *
       * Sem caso especial, e é o ponto: houve um momento em que o duelo tinha de
       * se calar, porque o número não atravessava o fio e o projector preenchia
       * `1` - e a barra imprimia "Nível 1" numa carta de nível 6. O nível passou
       * a viajar na metade privada da cadeira, portanto os dois modos sabem-no e
       * nenhum precisa de uma regra própria.
       */
      level: card.level,
      displayName: definition?.displayName ?? null,
    };

    /*
     * ACTIVA vence tudo o que vem a seguir, morte incluída.
     *
     * Uma habilidade a correr é estado da simulação: ela acaba quando o seu
     * relógio acaba, e não quando a interface deixa de gostar do mundo. Uma
     * barra que a apagasse ao morrer estaria a mentir sobre o que a arena está
     * a fazer nesse mesmo instante.
     */
    if (card.activeRemainingMs > 0) {
      return {
        ...common,
        state: 'active' as const,
        remainingMs: card.activeRemainingMs,
        totalMs: cardAbilityDurationMs(card.definitionId),
        blockedReason: null,
        actionable: false,
      };
    }

    /* Bloqueios, pela ordem em que se tornam verdadeiros. */
    const blocked = blockedReasonFor(card, context);
    if (blocked !== null) {
      return {
        ...common,
        state: 'blocked' as const,
        remainingMs: 0,
        totalMs: 0,
        blockedReason: blocked,
        actionable: false,
      };
    }

    if (card.cooldownRemainingMs > 0) {
      return {
        ...common,
        state: 'cooldown' as const,
        remainingMs: card.cooldownRemainingMs,
        totalMs: context.cooldownTotalsMs?.[slot] ?? cardAbilityCooldownMs(card),
        blockedReason: null,
        actionable: false,
      };
    }

    return {
      ...common,
      state: 'available' as const,
      remainingMs: 0,
      totalMs: 0,
      blockedReason: null,
      actionable: true,
    };
  });
}

/**
 * A razão do bloqueio, ou `null`.
 *
 * O modo primeiro: uma carta que não funciona aqui está bloqueada mesmo com a
 * nave viva e o arrefecimento a zero, e dizer-lhe "recarregando" seria prometer
 * que ela vai poder.
 */
function blockedReasonFor(
  card: CardAbilityState,
  context: CardHudContext,
): CardSlotBlockedReason | null {
  if (!cardWorksIn(card.definitionId, context.mode)) {
    return 'wrong_mode';
  }
  /*
   * **A pergunta é `cardAbilityIsImplemented`, e não a duração** (V28H.L).
   *
   * Ela foi a duração enquanto só existia uma habilidade, e era honesta: uma
   * carta sem implementação tem duração zero. Deixou de ser no dia em que
   * entraram cartas INSTANTÂNEAS - o Catolé sai e rebenta, a Esfera Ígnea sai
   * quando a carga acaba, e as duas têm duração zero por serem instantâneas e
   * não por serem inertes.
   *
   * Uma prova apanhou isto: as duas apareciam bloqueadas com "não
   * implementada" na barra, a funcionar perfeitamente no jogo.
   */
  if (!cardAbilityIsImplementedIn(card.definitionId, context.mode)) {
    return 'not_implemented';
  }
  if (!context.hasSession) {
    return 'no_session';
  }
  if (!context.shipAlive) {
    return 'ship_dead';
  }
  return null;
}

/**
 * A fracção JÁ DECORRIDA do que está a correr, entre 0 e 1.
 *
 * Decorrida e não restante, porque é assim que um preenchimento circular se lê:
 * ele enche à medida que o arrefecimento passa. Devolve 0 quando não há nada a
 * correr, o que deixa o anel vazio em vez de cheio.
 */
export function cardSlotProgress(view: CardSlotUiView): number {
  if (view.totalMs <= 0 || view.remainingMs <= 0) {
    return 0;
  }
  const elapsed = view.totalMs - view.remainingMs;
  return Math.min(1, Math.max(0, elapsed / view.totalMs));
}

/**
 * Os segundos que faltam, ARREDONDADOS PARA CIMA.
 *
 * Para cima porque um contador que mostrasse `0` durante os últimos 999 ms
 * estaria a dizer que a carta já pode - e o jogador carregaria na tecla para
 * nada. `1` até ao instante em que ela realmente fica disponível.
 */
export function cardSlotSeconds(view: CardSlotUiView): number {
  return view.remainingMs <= 0 ? 0 : Math.ceil(view.remainingMs / 1000);
}
